import { FC, PointerEvent, PropsWithChildren, ReactElement, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useEditor } from "@/utils/useEditor"
import { useAtom, useAtomValue } from "jotai"
import { saveCurrentAdjustmentState } from "@/store/store-history"
import { useObserveEditorViewport } from "@/utils/useObserveEditorViewport"
import { usePointerDrag } from "@/utils/usePointerDrag"
import { vec2 } from "gl-matrix"
import { project, unproject } from "@/core/viewport"
import { degrees, radians } from "@/core/utils/math"
import { cropAspectLockAtom, cropStraightenGridAtom } from "@/components/Crop/crop-ui-state"
import { Adjustments } from "@/core/types"
import {
	constrainCropStraighten,
	CropHandlerOptions,
	cropToLayer,
	handleCropMove,
	handleCropScale,
	layerToCrop,
	updateCropForTransforms,
} from "@/components/Crop/crop-interaction-handlers"
import { showingCropPanelAtom } from "@/store/ui-views-state"
import anchorCSS from "./CanvasCropOverlay.module.css"

import { getCanvasSize } from "@/core/utils/canvas"
import {
	AnchorPoint,
	anchorPoints,
	getRotationCursor,
	Rect,
} from "@/components/canvas-controls/canvas-controls-utils"
import { photoLoadingStateAtom } from "@/store/editor/photo-loading"
import { useShortcuts } from "@/utils/useShortcuts"
import { actionShortcuts } from "@/shortcuts/shortcuts"

export const CanvasCropOverlay: FC<PropsWithChildren> = () => {
	const { t } = useTranslation("components")
	const editor = useEditor()

	const loadingState = useAtomValue(photoLoadingStateAtom)
	const lockAspect = useAtomValue(cropAspectLockAtom)
	const [isCropActive, setIsCropActive] = useAtom(showingCropPanelAtom)
	const [showingStraightenGrid, showStraightenGrid] = useAtom(cropStraightenGridAtom)

	useShortcuts({
		keys: [actionShortcuts.enter, actionShortcuts.escape],
		action: () => {
			setIsCropActive(false)
		},
		trackingLabel: "exit_crop",
		disabled: !isCropActive,
	})

	const translateCrop = (vec: vec2) => {
		const layer = cropToLayer(editor.crop)
		const canvasRect = (editor.canvas as HTMLCanvasElement).getBoundingClientRect()

		const handlerOptions: CropHandlerOptions = {
			keepAspect: true,
			fromCenter: true,
			imageSize: editor.imageSize,
			pointer: getCoord(vec[0], vec[1], canvasRect),
			pointerStart: getCoord(0, 0, canvasRect),
			controlPoint: anchorPoints.center,
			adjustments: editor.adjustments,
			initialOrientation: editor.initialOrientation,
		}

		const updatedLayer = handleCropMove(layer, handlerOptions)
		const crop = layerToCrop(updatedLayer)

		editor.setAdjustments({
			crop,
		})
	}

	useShortcuts({
		keys: ["right", "left", "up", "down"],
		action: (event) => {
			event.preventDefault()
			switch (event.key) {
				case "ArrowRight":
					translateCrop([-1, 0])
					break
				case "ArrowLeft":
					translateCrop([1, 0])
					break
				case "ArrowUp":
					translateCrop([0, 1])
					break
				case "ArrowDown":
					translateCrop([0, -1])
					break
			}
		},
		trackingLabel: "move_crop",
		trackingValue: (event) => event.key,
		disabled: !isCropActive,
		preventDefault: true,
	})

	const viewMatrix = editor.mainViewport.viewMatrix
	const cropViewMatrixInv = editor.mainViewport.cropViewMatrixInv

	const updateCropWithTransforms = (adjustmentsOverride?: Partial<Adjustments>) => {
		const adjustments = adjustmentsOverride
			? { ...editor.adjustments, ...adjustmentsOverride }
			: editor.adjustments

		const updatedCrop = updateCropForTransforms(
			adjustments,
			editor.imageSize,
			editor.initialOrientation,
		)

		if (updatedCrop) {
			editor.setAdjustments({ crop: updatedCrop })
		}
	}

	useObserveEditorViewport()

	useEffect(() => {
		editor.setViewMode("crop")
		editor.setViewport({ relativeZoom: 0.9 })

		// Recalculate crop for transforms when entering crop mode
		updateCropWithTransforms()

		return () => {
			if (editor.viewMode === "crop") {
				editor.setViewport({
					offsetX: 0,
					offsetY: 0,
					relativeZoom: 1,
				})
				editor.setViewMode("after")
			}
		}
	}, [])

	useEffect(() => {
		if (loadingState !== "loaded" || !isCropActive) {
			return
		}

		if (editor.mainViewport.relativeZoom !== 0.9 || editor.viewMode !== "crop") {
			editor.setViewMode("crop")
			editor.setViewport({ relativeZoom: 0.9 })
		}

		// Recalculate crop for transforms when transform-related parameters change
		updateCropWithTransforms()
	}, [
		isCropActive,
		loadingState,
		editor.adjustments.flipX,
		editor.adjustments.flipY,
		editor.adjustments.orientation,
		editor.adjustments.perspectiveVertical,
		editor.adjustments.perspectiveHorizontal,
		editor.adjustments.perspectiveRotate,
		editor.adjustments.perspectiveScale,
		editor.adjustments.perspectiveAspect,
		editor.adjustments.perspectiveOffsetX,
		editor.adjustments.perspectiveOffsetY,
		editor.initialOrientation,
	])

	/* Actions */

	const confirmCrop = () => {
		setIsCropActive(false)
	}

	const getCoord = (x: number, y: number, rect: DOMRect) => {
		const mouse: vec2 = [x - rect.left, y - rect.top]
		const canvasSize = getCanvasSize(editor.canvas)
		const coord = unproject(canvasSize, mouse, cropViewMatrixInv)

		// TODO: better way to compute pointer coord?

		if (editor.adjustments.flipX) coord[0] = 1 - coord[0]
		if (editor.adjustments.flipY) coord[1] = 1 - coord[1]

		const rotation = editor.orientation * 90 + editor.adjustments.cropStraighten
		vec2.rotate(coord, coord, [0, 0], radians(rotation))

		return coord
	}

	const bindDragCrop = usePointerDrag<HTMLDivElement>({
		onPointerDown: (e) => {
			e.stopPropagation()
		},
		onDrag: (drag) => {
			const data = drag.getData(() => {
				const canvas = editor.canvas as HTMLCanvasElement

				return {
					imageSize: editor.imageSize,
					cropLayer: cropToLayer(editor.crop),
					canvasRect: canvas.getBoundingClientRect(),
					anchor: drag.target?.getAttribute("data-anchor") as AnchorPoint | null,
				}
			})

			if (!data.anchor) {
				return
			}

			let layer = data.cropLayer

			const pointerStart = getCoord(drag.startX, drag.startY, data.canvasRect)
			const pointer = getCoord(drag.x, drag.y, data.canvasRect)
			const controlPoint = anchorPoints[data.anchor] || anchorPoints.center
			const adjustments = editor.adjustments
			const initialOrientation = editor.initialOrientation

			const handlerOptions: CropHandlerOptions = {
				keepAspect: lockAspect || drag.event.shiftKey,
				fromCenter: drag.event.altKey,
				imageSize: data.imageSize,
				pointer,
				pointerStart,
				controlPoint,
				adjustments,
				initialOrientation,
			}

			if (data.anchor === "center") {
				layer = handleCropMove(layer, handlerOptions)
			} else {
				layer = handleCropScale(layer, handlerOptions)
			}

			editor.setAdjustments({
				crop: layerToCrop(layer),
			})

			if (drag.isEnd) {
				saveCurrentAdjustmentState({
					message: "Crop",
				})
			}
		},
	})

	const getCropRect = (): Rect => {
		const canvasSize = getCanvasSize(editor.canvas)

		const [x0, y0] = project(canvasSize, [-1, -1], viewMatrix)
		const [x1, y1] = project(canvasSize, [1, 1], viewMatrix)

		const viewLeft = Math.min(x0, x1)
		const viewRight = Math.max(x0, x1)
		const viewTop = Math.min(y0, y1)
		const viewBottom = Math.max(y0, y1)
		const viewWidth = viewRight - viewLeft
		const viewHeight = viewBottom - viewTop

		return {
			left: viewLeft,
			top: viewTop,
			width: viewWidth,
			height: viewHeight,
		}
	}

	const cropRect = getCropRect()

	const getPointerAngle = (e: PointerEvent<HTMLDivElement>) => {
		const target = e.currentTarget ?? e.target
		const rect = target.getBoundingClientRect()
		const x = e.pageX - rect.left
		const y = e.pageY - rect.top
		const cx = cropRect.left + cropRect.width / 2
		const cy = cropRect.top + cropRect.height / 2
		const dx = x - cx
		const dy = y - cy
		const angle = Math.atan2(dx, dy)

		return 180 - degrees(angle) // degrees clockwise, starts at 0
	}

	const bindDragStraighten = usePointerDrag<HTMLDivElement>({
		onPointerDown: (e) => {
			e.stopPropagation()
		},
		onDrag: (drag) => {
			const angle = getPointerAngle(drag.event)

			const data = drag.getData(() => {
				return {
					imageSize: editor.imageSize,
					startAngle: angle,
					crop: editor.adjustments.crop,
					cropStraighten: editor.adjustments.cropStraighten,
				}
			})

			const angleDelta = angle - data.startAngle
			const cropStraighten = (data.cropStraighten + angleDelta) % 360

			const crop = constrainCropStraighten(
				{ crop: data.crop, cropStraighten },
				data.imageSize,
			)

			editor.setAdjustments({ cropStraighten, crop })

			if (drag.isStart) {
				showStraightenGrid(true)
			}

			if (drag.isEnd) {
				showStraightenGrid(false)

				// Recalculate crop for transforms after straighten operation
				updateCropWithTransforms({ cropStraighten, crop })

				saveCurrentAdjustmentState({
					message: "Crop Straighten",
					changedValue: cropStraighten.toFixed(2),
				})
			}
		},
		onPointerMove: (e) => {
			const angle = getPointerAngle(e)
			const cursor = getRotationCursor(angle)

			e.currentTarget.style.cursor = cursor
		},
	})

	/* Rendering */

	const renderGrid = () => {
		const elements: ReactElement[] = []

		for (let i = 1; i <= 2; i++) {
			const p = (100 / 3) * i + "%"
			const lineStyle =
				"absolute bg-white opacity-50 pointer-events-none outline outline-1 outline-black/20"

			elements.push(
				<div key={`lh${i}`} className={`${lineStyle} h-px w-full`} style={{ top: p }} />,
			)
			elements.push(
				<div key={`lv${i}`} className={`${lineStyle} h-full w-px`} style={{ left: p }} />,
			)
		}

		return elements
	}

	const renderStraightenGrid = () => {
		const elements: ReactElement[] = []

		// Calculate grid size.
		// Find the shortest displayed image dimension (min(width, height)) and divide by 14
		const crop = editor.crop
		const imageSize: vec2 = [cropRect.width / crop[2], cropRect.height / crop[3]]
		const offset: vec2 = [imageSize[0] * crop[0], imageSize[1] * crop[1]]
		const gridSize = Math.min(...imageSize) / 14

		const lineStyle =
			"absolute bg-white opacity-50 pointer-events-none outline outline-1 outline-black/20"

		// Horizontal grid lines
		for (let i = gridSize; i < imageSize[0]; i += gridSize) {
			elements.push(
				<div
					key={`glv${i}`}
					className={`${lineStyle} h-full w-px`}
					style={{
						left: 0,
						transform: `translateX(${i - offset[0]}px)`,
					}}
				/>,
			)
		}

		// Vertical grid lines
		for (let i = gridSize; i < imageSize[1]; i += gridSize) {
			elements.push(
				<div
					key={`glh${i}`}
					className={`${lineStyle} h-px w-full`}
					style={{
						top: 0,
						transform: `translateY(${i - offset[1]}px)`,
					}}
				/>,
			)
		}

		return elements
	}

	const renderAnchor = (anchor: AnchorPoint, visible: boolean) => (
		<div
			className={`${anchorStyleCommon} ${anchorStyles[anchor]}`}
			style={{ opacity: visible ? 1 : 0 }}
			data-anchor={anchor}
			role="button"
			aria-label={t("canvasCropOverlay.cropHandle", { anchor: anchor })}
		/>
	)

	const renderAnchors = () => {
		const vertVisible = cropRect.width > 96 / 2
		const horzVisible = cropRect.height > 96 / 2
		const allVisible = cropRect.height > 48 / 2 && cropRect.width > 48 / 2

		return (
			<>
				{renderAnchor("top", vertVisible)}
				{renderAnchor("bottom", vertVisible)}

				{renderAnchor("left", horzVisible)}
				{renderAnchor("right", horzVisible)}

				{renderAnchor("topLeft", allVisible)}
				{renderAnchor("topRight", allVisible)}
				{renderAnchor("bottomLeft", allVisible)}
				{renderAnchor("bottomRight", allVisible)}
			</>
		)
	}

	const renderShade = () => {
		const cropTop = Math.max(0, cropRect.top)
		const cropLeft = Math.max(0, cropRect.left)
		const cropBottom = Math.max(0, cropTop + cropRect.height)
		const cropRight = Math.max(0, cropLeft + cropRect.width)

		return (
			<div
				className="absolute inset-0 overflow-hidden opacity-50"
				role="button"
				aria-label={t("canvasCropOverlay.ariaCropStraightenArea")}
				style={{ contain: "strict" }}
				{...bindDragStraighten}
			>
				<div
					className="pointer-events-none absolute bg-black"
					style={{ left: 0, right: 0, top: 0, height: cropTop }}
				/>
				<div
					className="pointer-events-none absolute bg-black"
					style={{ left: 0, right: 0, bottom: 0, top: cropBottom }}
				/>
				<div
					className="pointer-events-none absolute bg-black"
					style={{ bottom: 0, top: 0, left: 0, width: cropLeft }}
				/>
				<div
					className="pointer-events-none absolute bg-black"
					style={{ bottom: 0, top: 0, right: 0, left: cropRight }}
				/>
			</div>
		)
	}

	return (
		<div
			className="absolute inset-0 z-10 overflow-hidden"
			style={{ contain: "strict" }}
			onDoubleClick={confirmCrop}
		>
			{renderShade()}
			<div
				className="absolute cursor-move"
				data-anchor="center"
				draggable="false"
				role="button"
				aria-label={t("canvasCropOverlay.ariaCropArea")}
				style={{
					...cropRect,
					contain: "layout",
				}}
				{...bindDragCrop}
			>
				{renderAnchors()}
				<div
					className="pointer-events-none absolute size-full overflow-hidden"
					style={{ contain: "strict" }}
				>
					{showingStraightenGrid ? renderStraightenGrid() : renderGrid()}
				</div>
			</div>
		</div>
	)
}

const anchorStyleCommon = anchorCSS.common

const anchorStyles: Record<AnchorPoint, string> = {
	topLeft: anchorCSS.topLeft as string,
	topRight: anchorCSS.topRight as string,
	bottomRight: anchorCSS.bottomRight as string,
	bottomLeft: anchorCSS.bottomLeft as string,
	left: anchorCSS.left as string,
	right: anchorCSS.right as string,
	top: anchorCSS.top as string,
	bottom: anchorCSS.bottom as string,
	center: "",
}
